import { useState, useEffect, useMemo, useRef } from "react";
import { useDraftPersistence } from "@/hooks/useDraftPersistence";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import { Save, FileDown, Loader2, Sparkles } from "lucide-react";
import {
  SCORING_CATEGORIES,
  RESPONSE_OPTIONS,
  computeCategoryScore,
  computeGlobalScore,
  interpretCategoryScore,
  interpretGlobalScore,
} from "@/data/vitalityScoringData";
import { generateVitalityScoringPDF } from "@/utils/vitalityScoringPdfExport";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Cell,
} from "recharts";

export default function VitalityScoring() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientIdFromUrl = searchParams.get("patientId");
  const assessmentIdFromUrl = searchParams.get("assessmentId");

  const [patientId, setPatientId] = useState<string>(patientIdFromUrl || "");
  const [patientName, setPatientName] = useState<string>("");
  const [patients, setPatients] = useState<any[]>([]);
  const [assessmentDate, setAssessmentDate] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );
  const [responses, setResponses] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState("");
  const [bilanText, setBilanText] = useState("");
  const [isGeneratingBilan, setIsGeneratingBilan] = useState(false);
  const [assessmentId, setAssessmentId] = useState<string | null>(assessmentIdFromUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Local draft persistence — survives network errors / browser crashes
  const draftKey = assessmentId
    ? `vitality-scoring:${assessmentId}`
    : patientId
    ? `vitality-scoring:new:${patientId}`
    : null;
  const draftState = useMemo(
    () => ({ patientId, assessmentDate, responses, notes, bilanText }),
    [patientId, assessmentDate, responses, notes, bilanText],
  );
  const { storedDraft, clearDraft } = useDraftPersistence(draftKey, draftState, { delayMs: 2000 });
  const draftPromptedRef = useRef(false);

  useEffect(() => {
    if (!storedDraft || draftPromptedRef.current) return;
    if (isLoading) return;
    draftPromptedRef.current = true;
    const ageMs = Date.now() - new Date(storedDraft.savedAt).getTime();
    if (ageMs > 7 * 24 * 3600 * 1000) {
      clearDraft();
      return;
    }
    if (JSON.stringify(storedDraft.data) === JSON.stringify(draftState)) return;
    toast({
      title: "Brouillon non enregistré détecté",
      description: `Modifications du ${new Date(storedDraft.savedAt).toLocaleString("fr-FR")} restaurées.`,
    });
    const d: any = storedDraft.data;
    if (d.patientId) setPatientId(d.patientId);
    if (d.assessmentDate) setAssessmentDate(d.assessmentDate);
    if (d.responses) setResponses(d.responses);
    if (typeof d.notes === "string") setNotes(d.notes);
    if (typeof d.bilanText === "string") setBilanText(d.bilanText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedDraft, isLoading]);


  // Load patients
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("patients")
        .select("id, name, date_of_birth, gender")
        .eq("user_id", user.id)
        .order("name");
      if (data) setPatients(data);
    })();
  }, []);

  // Sync selected patient name
  useEffect(() => {
    const p = patients.find((p) => p.id === patientId);
    setPatientName(p?.name || "");
  }, [patientId, patients]);

  // Load existing assessment
  useEffect(() => {
    if (!assessmentIdFromUrl) return;
    setIsLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from("vitality_scoring_assessments")
        .select("*")
        .eq("id", assessmentIdFromUrl)
        .single();
      if (error) {
        toast({ title: "Erreur", description: "Bilan introuvable", variant: "destructive" });
      } else if (data) {
        setAssessmentId(data.id);
        setPatientId(data.patient_id || "");
        setAssessmentDate(data.assessment_date || new Date().toISOString().slice(0, 10));
        setResponses((data.responses as Record<string, number>) || {});
        setNotes(data.notes || "");
        setBilanText((data as any).bilan_text || "");
      }
      setIsLoading(false);
    })();
  }, [assessmentIdFromUrl]);

  const totalScore = useMemo(() => computeGlobalScore(responses), [responses]);
  const globalInterp = useMemo(() => interpretGlobalScore(totalScore), [totalScore]);
  const answeredCount = useMemo(
    () => Object.values(responses).filter((v) => v !== undefined && v !== null).length,
    [responses],
  );

  const chartData = useMemo(
    () =>
      SCORING_CATEGORIES.map((cat) => ({
        name: cat.title,
        short: cat.title.split(" ")[0],
        score: computeCategoryScore(cat, responses),
        fullMark: 20,
        color: cat.color,
      })),
    [responses],
  );

  const handleResponse = (questionId: string, value: number) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSave = async () => {
    if (!patientId) {
      toast({ title: "Consultant requis", description: "Sélectionnez un consultant.", variant: "destructive" });
      return;
    }
    setIsSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({ title: "Non connecté", variant: "destructive" });
      setIsSaving(false);
      return;
    }
    const scores: Record<string, number> = { total: totalScore };
    SCORING_CATEGORIES.forEach((cat) => {
      scores[cat.id] = computeCategoryScore(cat, responses);
    });

    const payload = {
      user_id: user.id,
      patient_id: patientId,
      assessment_date: assessmentDate,
      responses,
      scores,
      notes,
      bilan_text: bilanText,
    };

    let res;
    if (assessmentId) {
      res = await supabase
        .from("vitality_scoring_assessments")
        .update(payload)
        .eq("id", assessmentId)
        .select()
        .single();
    } else {
      res = await supabase
        .from("vitality_scoring_assessments")
        .insert(payload)
        .select()
        .single();
    }
    if (res.error) {
      toast({ title: "Erreur", description: res.error.message, variant: "destructive" });
    } else {
      setAssessmentId(res.data.id);
      clearDraft();
      toast({ title: "Bilan enregistré", description: "Le bilan de scoring a été sauvegardé." });
    }
    setIsSaving(false);
  };

  const [isExporting, setIsExporting] = useState(false);

  const buildBilanPayload = () => {
    const globalRaw = totalScore;
    const globalInterp = interpretGlobalScore(globalRaw);
    const pillarLabel = (raw: number) =>
      raw < 5 ? "Fragile" : raw < 10 ? "À améliorer" : raw < 15 ? "Correct" : "Bon";
    const categoriesPayload = SCORING_CATEGORIES.map((cat) => {
      const raw = computeCategoryScore(cat, responses);
      return { title: cat.title, score: raw, max: 20, label: pillarLabel(raw) };
    });
    return {
      globalScore: globalRaw,
      globalMax: 120,
      globalLabel: globalInterp.label,
      categories: categoriesPayload,
      patientName: patientName || "Consultant",
      notes,
    };
  };

  const handleGenerateBilan = async () => {
    if (Object.keys(responses).length === 0) {
      toast({ title: "Aucune réponse", description: "Renseignez le questionnaire avant de générer la lecture.", variant: "destructive" });
      return;
    }
    setIsGeneratingBilan(true);
    try {
      const { data, error } = await supabase.functions.invoke("vitality-bilan-summary", {
        body: buildBilanPayload(),
      });
      if (error) throw error;
      const text = (data as any)?.text || "";
      if (!text) throw new Error("Réponse IA vide");
      setBilanText(text);
      toast({ title: "Lecture générée", description: "Pensez à enregistrer pour la sauvegarder." });
    } catch (e: any) {
      console.error("Lïa bilan error:", e);
      toast({
        title: "Lecture du bilan indisponible",
        description: e?.message || "Réessayez dans un instant.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingBilan(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    const p = patients.find((p) => p.id === patientId);

    let text = bilanText;
    if (!text) {
      try {
        const { data, error } = await supabase.functions.invoke("vitality-bilan-summary", {
          body: buildBilanPayload(),
        });
        if (error) throw error;
        text = (data as any)?.text || "";
      } catch (e: any) {
        console.error("Lïa bilan error:", e);
        toast({
          title: "Lecture du bilan indisponible",
          description: "Le PDF sera généré sans la lecture personnalisée.",
          variant: "destructive",
        });
      }
    }

    await generateVitalityScoringPDF(
      { responses, notes, assessment_date: assessmentDate },
      {
        name: patientName || "consultant",
        date_of_birth: p?.date_of_birth,
        gender: p?.gender,
      },
      text,
    );
    setIsExporting(false);
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Bilan de vitalité — Questionnaire de scoring"
        subtitle="Évaluez la vitalité globale du consultant à travers 6 axes."
        icon={<Sparkles className="w-6 h-6" />}
      />

      {/* Top settings */}
      <Card className="mb-6 border-border/60 shadow-sm">
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Consultant *</Label>
            <Select value={patientId || "none"} onValueChange={(v) => setPatientId(v === "none" ? "" : v)}>
              <SelectTrigger className="border-border/60">
                <SelectValue placeholder="Sélectionner un consultant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Aucun —</SelectItem>
                {patients.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Date du bilan</Label>
            <Input
              type="date"
              value={assessmentDate}
              onChange={(e) => setAssessmentDate(e.target.value)}
              className="border-border/60"
            />
          </div>
          <div className="space-y-2">
            <Label>Progression</Label>
            <div className="space-y-1">
              <Progress value={(answeredCount / 30) * 100} />
              <p className="text-xs text-muted-foreground">{answeredCount} / 30 questions</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Live score panel */}
      <Card
        className="mb-6 border-0 shadow-lg overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${globalInterp.color}22, ${globalInterp.color}10)` }}
      >
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="flex flex-col items-center justify-center text-center">
              <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">
                Score global de vitalité
              </p>
              <p className="text-6xl font-bold" style={{ color: globalInterp.color }}>
                {totalScore}
                <span className="text-2xl text-muted-foreground">/120</span>
              </p>
              <Badge
                className="mt-3 text-white border-0 text-sm px-4 py-1"
                style={{ backgroundColor: globalInterp.color }}
              >
                {globalInterp.label}
              </Badge>
            </div>

            <div className="lg:col-span-2 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={chartData}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis dataKey="short" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 20]} tick={{ fontSize: 10 }} />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke={globalInterp.color}
                    fill={globalInterp.color}
                    fillOpacity={0.5}
                  />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bar chart */}
          <div className="mt-6 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="short" tick={{ fontSize: 11 }} interval={0} />
                <YAxis domain={[0, 20]} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Action buttons (top) */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Button onClick={handleSave} disabled={isSaving} className="w-full sm:w-auto">
          {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
        <Button variant="outline" onClick={handleExport} disabled={isExporting} className="w-full sm:w-auto">
          {isExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
          {isExporting ? "Génération…" : "Exporter PDF"}
        </Button>
        {patientId && (
          <Button variant="ghost" onClick={() => navigate(`/patients/${patientId}`)} className="w-full sm:w-auto">
            Voir la fiche consultant
          </Button>
        )}
      </div>

      {/* Categories */}
      <div className="space-y-6">
        {SCORING_CATEGORIES.map((cat) => {
          const catScore = computeCategoryScore(cat, responses);
          const interp = interpretCategoryScore(catScore);
          return (
            <Card key={cat.id} className="border-border/60 shadow-sm overflow-hidden">
              <CardHeader
                className="border-b"
                style={{ background: `linear-gradient(90deg, ${cat.color}15, transparent)` }}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="text-2xl">{cat.emoji}</span>
                      {cat.title}
                    </CardTitle>
                    <CardDescription>{cat.objective}</CardDescription>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{ color: cat.color }}>
                      {catScore}<span className="text-sm text-muted-foreground">/20</span>
                    </p>
                    <Badge
                      className="text-white border-0 mt-1"
                      style={{ backgroundColor: interp.color }}
                    >
                      {interp.label}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-5">
                {cat.questions.map((q) => (
                  <div key={q.id} className="space-y-2">
                    <Label className="text-sm font-medium">{q.label}</Label>
                    <RadioGroup
                      value={responses[q.id]?.toString() ?? ""}
                      onValueChange={(v) => handleResponse(q.id, parseInt(v))}
                      className="grid grid-cols-2 sm:grid-cols-5 gap-2"
                    >
                      {RESPONSE_OPTIONS.map((opt) => {
                        const isSel = responses[q.id] === opt.value;
                        return (
                          <label
                            key={opt.value}
                            htmlFor={`${q.id}_${opt.value}`}
                            className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                              isSel
                                ? "border-2 shadow-sm"
                                : "border-border/60 hover:border-primary/40"
                            }`}
                            style={isSel ? { borderColor: cat.color, backgroundColor: `${cat.color}15` } : undefined}
                          >
                            <RadioGroupItem value={opt.value.toString()} id={`${q.id}_${opt.value}`} />
                            <span className="text-xs sm:text-sm">
                              {opt.label} <span className="text-muted-foreground">({opt.value})</span>
                            </span>
                          </label>
                        );
                      })}
                    </RadioGroup>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Notes */}
      <Card className="mt-6 border-border/60 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Notes du praticien</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Observations, axes prioritaires, recommandations…"
            rows={5}
            className="border-border/60"
          />
        </CardContent>
      </Card>

      {/* Lecture du bilan (IA) */}
      <Card className="mt-6 border-border/60 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Lecture du bilan (IA)
            </CardTitle>
            <CardDescription>
              Texte généré automatiquement, modifiable, enregistré avec le bilan et imprimé sur le PDF.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleGenerateBilan}
            disabled={isGeneratingBilan}
          >
            {isGeneratingBilan ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {bilanText ? "Régénérer" : "Générer"}
          </Button>
        </CardHeader>
        <CardContent>
          <Textarea
            value={bilanText}
            onChange={(e) => setBilanText(e.target.value)}
            placeholder="Cliquez sur « Générer » pour obtenir une lecture personnalisée du bilan, ou écrivez-la vous-même…"
            rows={8}
            className="border-border/60"
          />
        </CardContent>
      </Card>


      {/* Actions bottom */}
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <Button onClick={handleSave} disabled={isSaving} className="w-full sm:w-auto">
          {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
        <Button variant="outline" onClick={handleExport} disabled={isExporting} className="w-full sm:w-auto">
          {isExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
          {isExporting ? "Génération…" : "Exporter PDF"}
        </Button>
      </div>
    </PageContainer>
  );
}
